import api from '@/services/api';
import { getDeviceId } from '@/utils/deviceId';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';
import { AUTH_CONFIG } from '@/config/constants';
import { setOrgId, setCompanyName } from '@/api/endpoints';
import {
  persistTokens,
  getRefreshToken,
  clearAuthStorage,
  isAccessTokenFresh,
} from '@/utils/authSession';
import { applyAccessTokenClaims, enrichUserWithTeamLeaderAccess } from '@/shared/lib/teamLeaderAccess';
import { extractPermissionsFromPayload } from '@/shared/access/normalizePermissions';
import { readAccessTokenClaims } from '@/shared/access/tokenClaims';

function persistLoginResult(data, credentials = {}) {
  const payload = data.data || {};
  const userData = payload.user || {};
  const employeeData = payload.employee || {};
  const identifier = credentials.identifier || credentials.username;
  const emailFromIdentifier =
    identifier && typeof identifier === 'string' && identifier.includes('@')
      ? identifier
      : null;

  const tokenClaims = readAccessTokenClaims(payload.token) || {};
  const isSuperAdmin = Boolean(
    tokenClaims.isSuperAdmin ||
      tokenClaims.role === 'superAdmin' ||
      payload.isSuperAdmin ||
      userData.isSuperAdmin
  );
  const userToStore = applyAccessTokenClaims({
    id: userData.id || userData.userId || tokenClaims.userId,
    userId: userData.userId || userData.id || tokenClaims.userId,
    username: userData.username || tokenClaims.username,
    email: userData.email || emailFromIdentifier,
    role: isSuperAdmin ? 'superAdmin' : (userData.role || tokenClaims.role),
    firstName: userData.firstName,
    lastName: userData.lastName,
    department: userData.department,
    position: userData.position,
    gender: userData.gender,
    isSuperAdmin,
    permissions: extractPermissionsFromPayload({
      ...payload,
      permissions: tokenClaims.permissions || payload.permissions || userData.permissions,
      user: userData,
    }),
    teamLeaderDepartments:
      tokenClaims.teamLeaderDepartments ||
      userData.teamLeaderDepartments ||
      payload.teamLeaderDepartments ||
      payload.employee?.teamLeaderDepartments ||
      [],
  }, payload.token);

  persistTokens({
    token: payload.token,
    refreshToken: payload.refreshToken,
    tenantId: payload.tenantId,
  });
  localStorage.setItem(AUTH_CONFIG.USER_STORAGE_KEY, JSON.stringify(userToStore));

  if (employeeData.employeeId) {
    localStorage.setItem(AUTH_CONFIG.EMPLOYEE_ID_STORAGE_KEY, employeeData.employeeId);
  }
  if (payload.orgId) setOrgId(payload.orgId);
  else if (payload.organization?.orgId) setOrgId(payload.organization.orgId);
  if (payload.companyName) setCompanyName(payload.companyName);

  return userToStore;
}

const authService = {
  login: async (credentials) => {
    try {
      const deviceId = getDeviceId();
      const loginData = {
        identifier: credentials.identifier || credentials.username,
        password: credentials.password
      };
      if (credentials.orgId) loginData.orgId = credentials.orgId;

      const response = await api.post(getEndpointPath(ENDPOINTS.AUTH.LOGIN), loginData, {
        headers: {
          'X-Device-ID': deviceId
        }
      });
      const data = response.data;

      if (!data.success && (data.code === 'MULTIPLE_ORGANIZATIONS' || data.code === 'ORG_SELECTION_REQUIRED')) {
        return data;
      }

      if (data.success && data.data && data.data.token) {
        persistLoginResult(data, credentials);
        await enrichUserWithTeamLeaderAccess(authService.getUser());
        return data;
      }
      throw new Error(data.message || 'لم يتم إرجاع توكن صحيح');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'حدث خطأ أثناء تسجيل الدخول';
      const apiError = new Error(errorMessage);
      apiError.response = error.response;
      throw apiError;
    }
  },

  loginSuperAdmin: async (credentials) => {
    const loginData = {
      identifier: credentials.identifier || credentials.username,
      password: credentials.password,
    };
    const response = await api.post(getEndpointPath(ENDPOINTS.AUTH.SUPER_ADMIN_LOGIN), loginData);
    const data = response.data;
    if (data.success && data.data && data.data.token) {
      persistLoginResult(data, credentials);
      return data;
    }
    throw new Error(data.message || 'لم يتم إرجاع توكن صحيح');
  },

  logout: async () => {
    try {
      const refreshToken = getRefreshToken();
      const response = await api.post(getEndpointPath(ENDPOINTS.AUTH.LOGOUT), { refreshToken });

      if (response.data?.success && response.data?.message) {
        console.log('✅ Logout success:', response.data.message);
      }

      clearAuthStorage();
      return response.data;
    } catch (error) {
      console.error('🚨 Logout API error:', error);
      clearAuthStorage();
      throw error;
    }
  },

  getToken: () => {
    return localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
  },

  getUser: () => {
    const user = localStorage.getItem(AUTH_CONFIG.USER_STORAGE_KEY);
    return user ? JSON.parse(user) : null;
  },

  getCurrentUser: () => {
    const user = localStorage.getItem(AUTH_CONFIG.USER_STORAGE_KEY);
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(AUTH_CONFIG.USER_STORAGE_KEY);
    const refreshToken = getRefreshToken();

    if (!user) return false;
    if (isAccessTokenFresh(token)) return true;
    // Cookie session can refresh an expired access token after reload.
    return Boolean(refreshToken || token);
  },

  /**
   * Permanently delete the authenticated user's account and associated data.
   * Same endpoint as the mobile app: DELETE /auth/delete-account
   */
  deleteAccount: async () => {
    try {
      const response = await api.delete(getEndpointPath(ENDPOINTS.AUTH.DELETE_ACCOUNT));
      const data = response.data || {};
      clearAuthStorage();
      if (data.success === false) {
        return {
          success: false,
          message: data.message || 'Failed to delete account',
        };
      }
      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      clearAuthStorage();
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to delete account';
      return { success: false, message: errorMessage };
    }
  },
};

export default authService;
